import {
  Ban,
  CalendarPlus,
  Check,
  ClipboardCheck,
  Copy,
  Eye,
  GitBranch,
  Link2,
  MailWarning,
  MoreHorizontal,
  RotateCw,
  XCircle
} from "lucide-react";
import { useMemo, useState, type FC } from "react";

import type {
  OutgoingIntake,
  OutgoingIntakeFormNode,
  OutgoingIntakeSubmission
} from "@/api/types/outgoingIntake";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { StatusBadge } from "@/components/ui/status-badge";
import {
  describeShowIf,
  showIfChipLabel
} from "@/features/foreignNationalOutgoingIntakes/conditional";
import {
  answeredIdsOf,
  estimateLine,
  intakeEstimate
} from "@/features/foreignNationalOutgoingIntakes/intakeEstimate";
import { PageBar, RailSection } from "@/features/intakeBuilder";
import { cn } from "@/lib/utils";

import { ActivityList } from "../components/ActivityList";
import { useRequestsIde } from "../context/RequestsIdeContext";
import { RemindPopover } from "../editor/RemindPopover";
import { SendSheet } from "../editor/SendSheet";
import { trackProgress, type TrackStep } from "../editor/trackProgress";
import { firstName } from "../editor/useSubmissionReview";
import { intakeActivity, scheduledReminders } from "../intakeActivity";
import { statusTone } from "../list/listModel";
import { asDate, clockLabel, displayStatus, previewArtifact } from "../model";
import { submissionArtifactId } from "../types";
import { useIntakeActions } from "../useIntakeActions";
import { PreviewDialog } from "./PreviewDialog";

type Props = {
  intake: OutgoingIntake;
  schema: OutgoingIntakeFormNode[];
  submissions: OutgoingIntakeSubmission[];
  clientEmail: string | null;
  clientName: string;
  preview?: boolean;
  onClosePreview?: () => void;
};

const WITH_CLIENT = new Set(["sent", "in_progress", "revision_requested"]);
const EXTEND_DAYS = [7, 14, 30] as const;

/** The backend refuses to resend while a submission is under review or out for revision. */
const SEND_AGAIN_STATUSES = new Set<OutgoingIntake["status"]>([
  "sent",
  "in_progress",
  "expired"
]);

const dayLabel = (value: unknown) =>
  asDate(value)?.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric"
  }) ?? "";

const StepState: FC<{ step: TrackStep }> = ({ step }) => {
  if (step.state === "answered")
    return (
      <span className="inline-flex shrink-0 items-center gap-1 text-sm text-success">
        <Check className="size-3" /> Answered
      </span>
    );
  if (step.state === "stopped")
    return <span className="shrink-0 text-sm text-accent">Stopped Here</span>;
  if (step.state === "skipped")
    return <span className="shrink-0 text-sm text-fg-3">Skipped</span>;
  return null;
};

type Confirm = "revoke" | "cancel" | null;

/**
 * A sent request: where the link stands (recipient, expiry, delivery), how
 * far the client got, what happened when, and what the attorney can do about
 * it (remind, extend, copy, send again, revoke, cancel, duplicate). The
 * questions are read-only.
 */
export const TrackPage: FC<Props> = ({
  intake,
  schema,
  submissions,
  clientEmail,
  clientName,
  preview = false,
  onClosePreview
}) => {
  const { open, openList } = useRequestsIde();
  const actions = useIntakeActions();
  const [sendOpen, setSendOpen] = useState(false);
  const [confirm, setConfirm] = useState<Confirm>(null);

  const first = firstName(clientName) || "the client";
  const withClient = WITH_CLIENT.has(intake.status);
  const cancelled = Boolean(intake.cancelled_at);
  const revoked = Boolean(intake.link_revoked_at);
  const live = withClient && !cancelled && !revoked;
  const submitted =
    intake.status === "awaiting_review" || intake.status === "accepted";
  const canSendAgain = !cancelled && SEND_AGAIN_STATUSES.has(intake.status);
  const canExtend = !submitted && !cancelled && !revoked;

  const latest = useMemo(
    () =>
      submissions.find(
        (s) => s.outgoing_intake_submission_id === intake.latest_submission_id
      ) ??
      submissions
        .slice()
        .sort((a, b) => b.revision_number - a.revision_number)[0] ??
      null,
    [intake.latest_submission_id, submissions]
  );
  const answers = useMemo(
    () =>
      submitted || !intake.progress_answers?.length
        ? latest?.answers ?? intake.progress_answers ?? []
        : intake.progress_answers,
    [intake.progress_answers, latest, submitted]
  );
  const progress = useMemo(
    () => trackProgress(schema, answers, withClient),
    [answers, schema, withClient]
  );
  const activity = useMemo(
    () => intakeActivity(intake, submissions),
    [intake, submissions]
  );
  const nextReminder = useMemo(
    () => scheduledReminders(intake)[0] ?? null,
    [intake]
  );
  const estimate = useMemo(
    () => intakeEstimate(schema, answeredIdsOf(answers)),
    [answers, schema]
  );

  const openReview = () =>
    latest &&
    open({
      kind: "submission",
      id: submissionArtifactId(
        intake.outgoing_intake_id,
        latest.outgoing_intake_submission_id
      ),
      title: `${intake.title} · Submission #${latest.revision_number}`
    });

  const expires = asDate(intake.expires_at);
  const expired =
    intake.status === "expired" ||
    (expires ? expires.getTime() < Date.now() : false);
  const lastActive = intake.progress_saved_at ?? intake.last_opened_at;
  const progressMeta = [
    intake.opened_at
      ? `Opened ${clockLabel(intake.opened_at)}`
      : withClient
      ? "Not opened yet"
      : null,
    lastActive ? `last active ${clockLabel(lastActive)}` : null,
    live && nextReminder ? `next reminder ${dayLabel(nextReminder.at)}` : null
  ]
    .filter(Boolean)
    .join(" · ");
  const linkState = cancelled
    ? `Cancelled ${dayLabel(intake.cancelled_at)}`
    : revoked
    ? `Link revoked ${dayLabel(intake.link_revoked_at)}`
    : expires && !submitted
    ? `${expired ? "Expired" : "Expires"} ${dayLabel(expires)}`
    : null;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PageBar
        crumbs={[{ label: "Intake Requests", onClick: () => openList() }]}
        title={intake.title || "Intake"}
        meta={
          <StatusBadge tone={statusTone(intake)}>
            {displayStatus(intake)}
          </StatusBadge>
        }
        actions={
          <>
            {intake.has_public_link && !revoked && !cancelled ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void actions.copyLink(intake)}
              >
                <Link2 /> Copy Link
              </Button>
            ) : null}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => open(previewArtifact(intake))}
            >
              <Eye /> Preview
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon-sm" aria-label="More">
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                {latest ? (
                  <DropdownMenuItem onSelect={openReview}>
                    <ClipboardCheck /> Review Submission
                  </DropdownMenuItem>
                ) : null}
                {canSendAgain ? (
                  <DropdownMenuItem onSelect={() => setSendOpen(true)}>
                    <RotateCw /> Send Again with a New Link…
                  </DropdownMenuItem>
                ) : null}
                <DropdownMenuItem
                  onSelect={() => void actions.duplicate(intake, schema)}
                >
                  <Copy /> Duplicate as Draft
                </DropdownMenuItem>
                {live || (!cancelled && intake.status !== "accepted") ? (
                  <DropdownMenuSeparator />
                ) : null}
                {live ? (
                  <DropdownMenuItem onSelect={() => setConfirm("revoke")}>
                    <Ban /> Revoke Link…
                  </DropdownMenuItem>
                ) : null}
                {!cancelled && intake.status !== "accepted" ? (
                  <DropdownMenuItem
                    variant="destructive"
                    onSelect={() => setConfirm("cancel")}
                  >
                    <XCircle /> Cancel Request…
                  </DropdownMenuItem>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
            {live ? (
              <RemindPopover intake={intake} clientFirstName={first} />
            ) : intake.status === "awaiting_review" && latest ? (
              <Button variant="primary" size="sm" onClick={openReview}>
                <ClipboardCheck /> Review
              </Button>
            ) : null}
          </>
        }
      />

      {intake.email_bounced_at && withClient ? (
        <div className="flex min-w-0 items-center gap-2 border-b border-line-1 bg-danger-soft px-4 py-2 text-sm text-danger">
          <MailWarning className="size-3.5 shrink-0" />
          <span className="min-w-0 flex-1">
            The email to {intake.recipient_email ?? first} bounced
            {intake.email_bounce_reason
              ? ` (${intake.email_bounce_reason})`
              : ""}
            . Copy the link and send it another way, or send again to a
            corrected address.
          </span>
          {intake.has_public_link ? (
            <Button
              variant="secondary"
              size="xs"
              onClick={() => void actions.copyLink(intake)}
            >
              Copy Link
            </Button>
          ) : null}
          {canSendAgain ? (
            <Button
              variant="secondary"
              size="xs"
              onClick={() => setSendOpen(true)}
            >
              Send Again
            </Button>
          ) : null}
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1">
        <div className="min-w-0 flex-1 overflow-y-auto">
          <div className="mx-auto flex max-w-180 flex-col gap-5 px-6 py-6">
            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <span className="text-md font-medium text-fg">
                  {submitted
                    ? `Submission #${latest?.revision_number ?? 1} received`
                    : `${progress.answered} of ${progress.total} answered`}
                </span>
                {progressMeta ? (
                  <span className="text-sm text-fg-3">{progressMeta}</span>
                ) : null}
              </div>
              <div className="flex gap-1" aria-hidden>
                {progress.steps
                  .filter((s) => s.state !== "skipped")
                  .map((s) => (
                    <span
                      key={String(s.node.node_id)}
                      className={cn(
                        "h-1 flex-1 rounded-full",
                        s.state === "answered"
                          ? "bg-accent"
                          : s.state === "stopped"
                          ? "bg-accent-soft"
                          : "bg-fill-3"
                      )}
                    />
                  ))}
              </div>
            </div>

            <div className="overflow-hidden rounded-xl bg-elevated ring-1 ring-line-2">
              <div className="flex h-10 items-center gap-2 bg-fill-1 px-3">
                <span className="text-base font-medium text-fg">Questions</span>
                <span className="min-w-0 flex-1 truncate text-right text-sm text-fg-3">
                  {estimateLine(estimate, {
                    progress: live && !submitted,
                    due: live && !submitted && !expired ? expires : null
                  })}
                </span>
              </div>
              <ol className="divide-y divide-line-1 border-t border-line-2">
                {progress.steps.map((s) => {
                  const chip = showIfChipLabel(s.node, schema);
                  return (
                    <li
                      key={String(s.node.node_id)}
                      className={cn(
                        "flex min-h-9 min-w-0 items-center gap-2 px-3 text-base",
                        s.state === "skipped" ? "text-fg-3" : "text-fg"
                      )}
                    >
                      <span className="w-5 shrink-0 text-right text-sm text-fg-3 tabular-nums">
                        {s.n ?? "–"}
                      </span>
                      <span className="min-w-0 flex-1 truncate" title={s.label}>
                        {s.label}
                      </span>
                      {chip ? (
                        <span
                          className="inline-flex shrink-0 items-center gap-1 text-sm text-fg-3"
                          title={describeShowIf(s.node, schema) ?? undefined}
                        >
                          <GitBranch className="size-3" /> {chip}
                        </span>
                      ) : null}
                      <StepState step={s} />
                    </li>
                  );
                })}
              </ol>
            </div>
          </div>
        </div>

        <aside
          className="flex w-72 shrink-0 flex-col gap-6 overflow-y-auto border-l border-line-1 px-4 py-5"
          aria-label="Request Details"
        >
          <RailSection
            title="Link"
            action={
              canExtend ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="xs">
                      Extend
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44">
                    <DropdownMenuLabel className="text-sm font-normal text-fg-3">
                      Keep the same link working
                    </DropdownMenuLabel>
                    {EXTEND_DAYS.map((d) => (
                      <DropdownMenuItem
                        key={d}
                        onSelect={() => void actions.extend(intake, d)}
                      >
                        <CalendarPlus /> {d} More Days
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : null
            }
          >
            <div className="flex flex-col gap-1 text-sm text-fg-2">
              {intake.recipient_email ? (
                <span className="truncate">
                  Sent to {intake.recipient_email}
                </span>
              ) : null}
              {linkState ? <span>{linkState}</span> : null}
              <span className="text-fg-3">Questions are read-only.</span>
            </div>
          </RailSection>
          <RailSection title="Activity">
            <ActivityList items={activity} clientLabel={first} />
          </RailSection>
        </aside>
      </div>

      <SendSheet
        intake={sendOpen ? intake : null}
        schema={schema}
        clientEmail={clientEmail}
        clientName={clientName}
        onOpenChange={setSendOpen}
      />
      <PreviewDialog
        open={preview}
        onOpenChange={(o) => (o ? undefined : onClosePreview?.())}
        intake={intake}
        schema={schema}
        clientName={clientName}
      />

      <AlertDialog
        open={confirm !== null}
        onOpenChange={(o) => !o && setConfirm(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm === "revoke"
                ? `Revoke ${first}'s link?`
                : `Cancel “${intake.title}”?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === "revoke"
                ? `The link stops working right away and ${first} sees that it was turned off. Her saved answers stay. Send again to give her a new link.`
                : `${first} can no longer open or submit it, and no more reminders go out. Anything she already submitted stays in the review.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep It</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (confirm === "revoke") void actions.revoke(intake);
                else void actions.cancel(intake);
                setConfirm(null);
              }}
            >
              {confirm === "revoke" ? "Revoke Link" : "Cancel Request"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
